import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const languages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', flag: '🇿🇦' },
  { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', flag: '🇿🇦' },
  { code: 'st', name: 'Sesotho', nativeName: 'Sesotho', flag: '🇿🇦' },
  { code: 'ts', name: 'Tsonga', nativeName: 'Xitsonga', flag: '🇿🇦' },
];

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('preferred_language', langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Language Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors border-2 border-ssdp-navy/20 min-h-[44px] min-w-[44px] focus:outline-none focus:ring-4 focus:ring-ssdp-teal focus:ring-offset-2"
        aria-label={`Select Language. Current language: ${currentLanguage.nativeName}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Globe className="w-5 h-5 text-ssdp-navy" aria-hidden="true" />
        <span className="text-sm font-bold text-ssdp-navy font-heading hidden sm:inline">
          {currentLanguage.flag} {currentLanguage.code.toUpperCase()}
        </span>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-64 bg-white rounded-xl border-3 border-ssdp-navy shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] z-50 overflow-hidden"
            role="menu"
            aria-label="Language selection menu"
          >
            <div className="p-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  data-testid={`lang-option-${lang.code}`}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-ssdp-teal focus:ring-inset ${
                    currentLanguage.code === lang.code
                      ? 'bg-ssdp-teal text-white'
                      : 'hover:bg-gray-100 text-ssdp-navy'
                  }`}
                  role="menuitem"
                  aria-current={currentLanguage.code === lang.code ? 'true' : undefined}
                  aria-label={`Switch to ${lang.nativeName}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl" role="img" aria-label={`${lang.name} flag`}>{lang.flag}</span>
                    <div className="text-left">
                      <div className={`font-bold text-sm font-heading ${
                        currentLanguage.code === lang.code ? 'text-white' : 'text-ssdp-navy'
                      }`}>
                        {lang.nativeName}
                      </div>
                      <div className={`text-xs font-body ${
                        currentLanguage.code === lang.code ? 'text-white/80' : 'text-gray-500'
                      }`}>
                        {lang.name}
                      </div>
                    </div>
                  </div>
                  {currentLanguage.code === lang.code && (
                    <Check className="w-5 h-5 text-white" aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
